import { Alert, Box, Checkbox, Divider, Grid, TextField, Typography, useTheme } from '@mui/material';
import { AnnotationClassRemovalOperation, TodoOption } from 'interfaces/annotation';
import { find, first, isEmpty, join, size } from 'lodash';
import React from 'react';
import { Controller, UseFormReturn } from 'react-hook-form';
import { humanize } from 'utils/helpers';
import { useCellTypeOptions } from 'utils/queryHooks/useCellTypeOptions';
import { AssignmentFormValues } from './AnnotationAssignmentDialog';
import { DuplicateTodoOptionGroup, UnconfiguredAnnotationValue } from './annotationClassOptions';
import ClassesToAnnotateList from './ClassesToAnnotateList';
import ClassesToAnnotateSelection from './ClassesToAnnotateSelection';
import DuplicateClassRecords from './DuplicateClassRecords';
import UnconfiguredAnnotationValues from './UnconfiguredAnnotationValues';

interface ClassesToAnnotateListProps {
  assignmentCreationForm: UseFormReturn<AssignmentFormValues, any>;
  onDeleteClassToAnnotate?: (id: string) => void;
  onReplaceClassToAnnotate?: (id: string) => void;
  isExistingAnnotations?: (currentClassToReplace: TodoOption) => boolean;
  onColorChange?: (id: string, color: string) => void;
  onClassesToAnnotateChange?: (newClassesToAnnotate: TodoOption[]) => void;
  classesToRemoveWithActions?: AnnotationClassRemovalOperation[];
  onEditDeleted?: (id: string) => void;
  onRestoreDeleted?: (id: string) => void;
  alreadyDeletedOptions?: TodoOption[];
  duplicateClassGroups?: DuplicateTodoOptionGroup[];
  onDeleteDuplicateClasses?: (classNames: string[]) => void;
  isDeletingDuplicateClasses?: boolean;
  skippedDuplicateClassNames?: string[];
  onDismissSkippedDuplicateClasses?: () => void;
  unconfiguredAnnotationValues?: UnconfiguredAnnotationValue[];
  onAddUnconfiguredAnnotationValue?: (value: UnconfiguredAnnotationValue) => void;
  duplicateNameKeys?: Set<string>;
}

const CreateAnnotationAssignmentSection: React.FC<ClassesToAnnotateListProps> = ({
  assignmentCreationForm,
  onDeleteClassToAnnotate,
  onReplaceClassToAnnotate,
  isExistingAnnotations,
  onColorChange,
  onClassesToAnnotateChange,
  classesToRemoveWithActions,
  onEditDeleted,
  onRestoreDeleted,
  alreadyDeletedOptions = [],
  duplicateClassGroups = [],
  onDeleteDuplicateClasses,
  isDeletingDuplicateClasses,
  skippedDuplicateClassNames = [],
  onDismissSkippedDuplicateClasses,
  unconfiguredAnnotationValues = [],
  onAddUnconfiguredAnnotationValue,
  duplicateNameKeys = new Set<string>(),
}) => {
  const {
    register,
    watch,
    control,
    formState: { errors },
  } = assignmentCreationForm;

  const { data: cellTypes } = useCellTypeOptions();
  const showWarningOnItem = (todo: TodoOption) => find(cellTypes, { id: todo.name })?.isAbstract;

  return (
    <Grid container height={800}>
      <Grid item xs={6} container direction="column" spacing={1} height="100%">
        <Grid item mx={2}>
          <Controller
            control={control}
            name="name"
            render={({ field: { onChange } }) => (
              <TextField
                required
                label="Assignment Name"
                {...register('name')}
                onChange={onChange}
                variant="outlined"
                margin="normal"
                error={Boolean(errors['name'])}
                helperText={humanize(errors['name']?.message)}
              />
            )}
          />
        </Grid>
        <Grid item mx={2}>
          <Controller
            control={control}
            name="isGlobal"
            render={({ field: { value, onChange } }) => (
              <Box display="flex" alignItems="center">
                <Checkbox
                  checked={!!value}
                  onChange={(e) => onChange(e.target.checked)}
                  sx={{ padding: 0, marginRight: 1 }}
                />
                <Typography>
                  <Typography variant="body1" component="span">
                    Global Assignment
                  </Typography>{' '}
                  <Typography variant="caption" component="span">
                    (assignment that is not related to a specific study)
                  </Typography>
                </Typography>
              </Box>
            )}
          />
        </Grid>
        <Grid item xs={true} sx={{ overflow: 'auto', width: '100%' }} ml={2} pr={2}>
          {!isEmpty(skippedDuplicateClassNames) && (
            <Alert severity="info" onClose={onDismissSkippedDuplicateClasses} sx={{ mb: 1.5 }}>
              {`Skipped duplicate ${skippedDuplicateClassNames.length === 1 ? 'class' : 'classes'}: ${join(
                skippedDuplicateClassNames,
                ', '
              )}`}
            </Alert>
          )}
          {Boolean(errors['todos']) && (
            <Typography variant="body2" color="error">
              {humanize(errors['todos']?.message)}
            </Typography>
          )}
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
            {`Active classes (${(first(watch('todos'))?.options ?? []).length})`}
          </Typography>
          {(watch('todos') ?? []).length === 0 ? (
            <ClassesToAnnotatePlaceHolder />
          ) : (
            <ClassesToAnnotateList
              classesToAnnotate={first(watch('todos'))?.options ?? []}
              onDeleteTodo={onDeleteClassToAnnotate}
              onReplaceClass={onReplaceClassToAnnotate}
              isExistingAnnotations={isExistingAnnotations}
              onColorChange={onColorChange}
              isEditable
              setClassesToAnnotate={onClassesToAnnotateChange}
              showWarningOnItem={showWarningOnItem}
              warningMessage="Not recommended, not a specific cell type"
              duplicateNameKeys={duplicateNameKeys}
            />
          )}
          {(!isEmpty(alreadyDeletedOptions) || !isEmpty(classesToRemoveWithActions)) && (
            <Box sx={{ mt: 2 }}>
              <Divider sx={{ mb: 1.5 }} />
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                {`Archived classes (${size(alreadyDeletedOptions) + size(classesToRemoveWithActions)})`}
              </Typography>
              <ClassesToAnnotateList
                classesToAnnotate={[]}
                classesToRemoveWithActions={classesToRemoveWithActions}
                alreadyDeletedOptions={alreadyDeletedOptions}
                isExistingAnnotations={isExistingAnnotations}
                onEditDeleted={onEditDeleted}
                onRestoreDeleted={onRestoreDeleted}
                duplicateNameKeys={duplicateNameKeys}
              />
            </Box>
          )}
          {!isEmpty(duplicateClassGroups) && onDeleteDuplicateClasses && (
            <Box sx={{ mt: 2 }}>
              <Divider sx={{ mb: 1.5 }} />
              <DuplicateClassRecords
                duplicateGroups={duplicateClassGroups}
                isDeleting={isDeletingDuplicateClasses}
                onDeleteDuplicates={onDeleteDuplicateClasses}
              />
            </Box>
          )}
          {!isEmpty(unconfiguredAnnotationValues) && onAddUnconfiguredAnnotationValue && (
            <Box sx={{ mt: 2 }}>
              <Divider sx={{ mb: 1.5 }} />
              <UnconfiguredAnnotationValues
                values={unconfiguredAnnotationValues}
                onAdd={onAddUnconfiguredAnnotationValue}
              />
            </Box>
          )}
        </Grid>
      </Grid>

      <ClassesToAnnotateSelection
        onClassesSelected={onClassesToAnnotateChange}
        selectedClassesToAnnotate={(first(watch('todos'))?.options ?? []) as TodoOption[]}
      />
    </Grid>
  );
};

const ClassesToAnnotatePlaceHolder: React.FC = () => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        backgroundColor: theme.palette.mode === 'light' ? theme.palette.grey[100] : theme.palette.grey[800],
        borderRadius: 2,
        padding: 4,
        marginY: 6,
        marginX: 10,
        textAlign: 'center',
        minHeight: 150,
      }}
    >
      <Typography variant="body1" fontWeight="bold" mb={1}>
        Please start adding classes to your assignment
      </Typography>
      <Typography variant="body2">you can add classes from a preset, taxonomy or copy assignments settings</Typography>
    </Box>
  );
};
export default CreateAnnotationAssignmentSection;
